import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import { DistancePipe } from '../../pipes/distance.pipe';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { HumanProfileResult } from '../../models/human-profile.models';
import { normalizeResultDiscipline } from '../../models/discipline.models';
import { TranslationService } from '../../services/translation.service';

type ProfileDiscipline = 'Cani-VTT' | 'Cani-Trottinette' | 'Canicross' | 'Cani-Marche';

interface DisciplineStats {
  code: ProfileDiscipline;
  labelKey: string;
  icon: string;
  races: number;
  distance: number;
  averageSpeed: number | null;
}

interface SpeedAccumulator {
  races: number;
  distance: number;
  timedDistance: number;
  timeSeconds: number;
  speedSum: number;
  speedCount: number;
}

@Component({
  selector: 'app-human-profile-stats',
  standalone: true,
  imports: [CommonModule, RouterModule, DistancePipe, TranslatePipe],
  templateUrl: './human-profile-stats.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HumanProfileStatsComponent {
  private readonly translationService = inject(TranslationService);

  readonly results = input<HumanProfileResult[]>([]);
  readonly points = input<number | null>(null);
  readonly showFavoriteDog = input(false);

  readonly totalRaces = computed(() => this.results().length);
  readonly totalDistance = computed(() => this.results().reduce(
    (total, result) => total + (result.race?.distanceKm || 0),
    0,
  ));
  readonly averageSpeed = computed(() => this.averageSpeedFor(this.results()));
  readonly bestRank = computed(() => {
    const ranks = this.results()
      .map(result => result.rank)
      .filter((rank): rank is number => rank !== undefined && rank !== null && rank > 0);
    return ranks.length > 0 ? Math.min(...ranks) : null;
  });
  readonly favoriteDog = computed(() => {
    const dogs = new Map<string, { id: string; name: string; count: number }>();
    this.results().forEach(result => {
      const dog = result.team?.dog;
      if (!dog) return;
      const current = dogs.get(dog.id);
      dogs.set(dog.id, { id: dog.id, name: dog.name, count: (current?.count ?? 0) + 1 });
    });

    return [...dogs.values()].sort((a, b) => b.count - a.count)[0] ?? null;
  });

  readonly disciplineStats = computed<DisciplineStats[]>(() => {
    const accumulators = new Map<ProfileDiscipline, SpeedAccumulator>();
    this.results().forEach(result => {
      const code = this.toProfileDiscipline(result);
      const current = accumulators.get(code) ?? {
        races: 0,
        distance: 0,
        timedDistance: 0,
        timeSeconds: 0,
        speedSum: 0,
        speedCount: 0,
      };
      const distance = result.race?.distanceKm || 0;
      current.races += 1;
      current.distance += distance;
      if (result.timeSeconds > 0 && distance > 0) {
        current.timedDistance += distance;
        current.timeSeconds += result.timeSeconds;
      }
      if (result.averageSpeedKmph && result.averageSpeedKmph > 0) {
        current.speedSum += result.averageSpeedKmph;
        current.speedCount += 1;
      }
      accumulators.set(code, current);
    });

    return this.disciplineDefinitions
      .map(definition => {
        const data = accumulators.get(definition.code);
        if (!data) return null;
        return {
          ...definition,
          races: data.races,
          distance: data.distance,
          averageSpeed: this.averageSpeedForAccumulator(data),
        };
      })
      .filter((item): item is DisciplineStats => item !== null);
  });

  private readonly disciplineDefinitions: Array<Pick<DisciplineStats, 'code' | 'labelKey' | 'icon'>> = [
    { code: 'Cani-VTT', labelKey: 'raceDetail.vtt', icon: '🚲' },
    { code: 'Cani-Trottinette', labelKey: 'raceDetail.trottinette', icon: '🛴' },
    { code: 'Canicross', labelKey: 'raceDetail.canicross', icon: '🏃' },
    { code: 'Cani-Marche', labelKey: 'raceDetail.marche', icon: '🥾' },
  ];

  formatSpeed(speed: number | null): string {
    if (speed === null || !Number.isFinite(speed)) return '--';
    const locale = this.translationService.currentLang() === 'fr' ? 'fr-FR' : 'en-US';
    return new Intl.NumberFormat(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(speed);
  }

  private toProfileDiscipline(result: HumanProfileResult): ProfileDiscipline {
    const normalized = normalizeResultDiscipline(
      result.race?.discipline ?? result.discipline,
      result.race?.name,
      result.category,
    );
    if (normalized === 'Cani-VTT') return 'Cani-VTT';
    if (normalized === 'Cani-Trottinette') return 'Cani-Trottinette';
    if (normalized === 'Cani-Marche') return 'Cani-Marche';
    return 'Canicross';
  }

  private averageSpeedFor(results: HumanProfileResult[]): number | null {
    const accumulator: SpeedAccumulator = {
      races: 0,
      distance: 0,
      timedDistance: 0,
      timeSeconds: 0,
      speedSum: 0,
      speedCount: 0,
    };
    results.forEach(result => {
      const distance = result.race?.distanceKm || 0;
      if (result.timeSeconds > 0 && distance > 0) {
        accumulator.timedDistance += distance;
        accumulator.timeSeconds += result.timeSeconds;
      }
      if (result.averageSpeedKmph && result.averageSpeedKmph > 0) {
        accumulator.speedSum += result.averageSpeedKmph;
        accumulator.speedCount += 1;
      }
    });
    return this.averageSpeedForAccumulator(accumulator);
  }

  private averageSpeedForAccumulator(accumulator: SpeedAccumulator): number | null {
    if (accumulator.timeSeconds > 0 && accumulator.timedDistance > 0) {
      return accumulator.timedDistance / (accumulator.timeSeconds / 3600);
    }
    return accumulator.speedCount > 0 ? accumulator.speedSum / accumulator.speedCount : null;
  }
}
